import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getAllContracts, getSupplierContracts } from '../../services/procurementService';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const ContractsHomePage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || t('offersSent', 'Offers Sent');

  const navigate = useNavigate();
  const isSupplier = localStorage.getItem('supplierId');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState(initialStatus);
  const userId = localStorage.getItem('userId') || '';

  const statusMap = {
    [t('active', 'Active')]: 0,
    [t('completed', 'Completed')]: 1,
    [t('expired', 'Expired')]: 2,
    [t('offersSent', 'Offers Sent')]: 3,
  };

  const { data: allContracts = [] } = useQuery({
    queryKey: ['allContracts', userId, activeStatus],
    queryFn: async () => {
      const statusMapping: { [key: string]: string } = {
        [t('offersSent', 'Offers Sent')]: 'pending',
        [t('active', 'Active')]: 'Accepted',
        [t('completed', 'Completed')]: 'Active',
        [t('expired', 'Expired')]: 'Overdue',
        default: 'Rejected',
      };

      const status =
        statusMapping[activeStatus as keyof typeof statusMapping] || statusMapping.default;

      const response = isSupplier
        ? await getSupplierContracts(status, isSupplier)
        : await getAllContracts(status);

      return response.data;
    },
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setSearchParams({ status: activeStatus });
  }, []);

  const handleStatusChange = (status: string) => {
    setActiveStatus(status);
    setSearchParams({ status });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="mx-auto max-w-screen-xl">
      <div className="p-8">
        <div className="mb-8">
          <h1 className="mb-1 text-2xl font-semibold text-gray-900">
            {t('contractManagement', 'Contract Management')}
          </h1>
          <p className="text-base text-[#525252]">
            {t('trackAndManageContracts', 'Track and manage all your contracts')}
          </p>
        </div>

        <div className="min-h-[80vh] w-full rounded-2xl border border-[#E5E5E5] bg-white p-8">
          <div className="relative mb-6">
            <input
              type="text"
              placeholder={t('search', 'Search')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3.5 py-2.5 pl-12 text-base text-gray-800"
            />
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-500"
              size={20}
            />
          </div>

          <>
            <div className="mb-6 flex space-x-10">
              {Object.keys(statusMap).map((status) => (
                <button
                  key={status}
                  className={`min-w-[106px] rounded-full px-3.5 py-2.5 text-base ${
                    activeStatus === status
                      ? 'bg-green-600 font-semibold text-white'
                      : 'bg-none text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => handleStatusChange(status)}
                >
                  {status}
                </button>
              ))}
            </div>

            {allContracts.length > 0 ? (
              <div className="overflow-auto rounded-xl border border-[#E4E7EC] shadow-sm">
                <div className="grid grid-cols-[1fr_1.3fr_.5fr_.5fr] gap-4 rounded-t-xl border-b border-[#D2D6DB] bg-[#F5F5F5] px-5 py-[14px] text-sm font-semibold text-[#40474F]">
                  <span>{t('businessName', 'Business Name')}</span>
                  <span>{t('contractName', 'Contract Name')}</span>
                  <span>{t('date', 'Date')}</span>
                  <span>{t('status', 'Status')}</span>
                </div>

                {allContracts.map((contractItem) => {
                  const currentStatus = searchParams.get('status');
                  return (
                    <div
                      role="link"
                      key={contractItem.contractId}
                      className="grid cursor-pointer grid-cols-[1fr_1.3fr_.5fr_.5fr] items-center gap-4 border-b border-[#D2D6DB] px-5 py-[14px]"
                      onClick={() => navigate(`/contracts/${contractItem.contractId}`)}
                    >
                      <span className="text-base font-semibold text-[#101828]">
                        {contractItem.businessName}
                      </span>
                      <span
                        className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-[#525252]"
                        title={contractItem.contractName}
                      >
                        {contractItem.contractName}
                      </span>
                      <span className="text-sm font-medium text-[#525252]">
                        {moment(contractItem?.milestone).format('DD/MM/YYYY')}
                      </span>
                      <span
                        className={`w-fit min-w-[93px] rounded-[60px] px-2.5 py-1 text-center text-sm font-medium text-white ${
                          currentStatus === t('active', 'Active') ||
                          currentStatus === t('completed', 'Completed')
                            ? 'bg-[#24A148]'
                            : currentStatus === t('offersSent', 'Offers Sent')
                              ? 'bg-[#134C9D]'
                              : 'bg-[#FA4D56]'
                        }`}
                      >
                        {contractItem?.status}
                      </span>
                    </div>
                  );
                })}

                <div className="flex items-center justify-between px-6 pb-4 pt-3">
                  <span className="text-sm text-[#344054]">
                    {t('pageXOfY', 'Page {current} of {total}', {
                      current: 1,
                      total: 10,
                    })}
                  </span>

                  <div className="flex gap-x-3">
                    <button className="rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm">
                      {t('previous', 'Previous')}
                    </button>
                    <button className="rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm">
                      {t('next', 'Next')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[40vh] items-center justify-center">
                <div>
                  <h3 className="text-center text-2xl font-semibold text-[#161616]">
                    {t('noActiveContracts', 'There are no active contracts')}
                  </h3>
                  <p className="text-center text-sm font-medium text-[#7F7F7F]">
                    {t(
                      'activeContractsWillAppear',
                      "Contracts you're actively working on will appear here"
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default ContractsHomePage;
